import React, { useState, useEffect } from "react";
import ReactLoading from "react-loading";
import "./audioplayercomponent.css";
import { Interview } from "../models/Interview";
import { fetchWithAuthentication } from "../utilities/FetchUtil";
import { InterviewResourceNotFoundError } from "../errors/InterviewResourceNotFoundError";
import { calculateDaysBetweenDates } from "../utilities/DateUtil";

interface AudioPlayerComponentProps {
  interview: Interview;
}

const AudioPlayerComponent = ({ interview }: AudioPlayerComponentProps) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const today = new Date();
  const dateOfCreation = new Date(interview.createdAt);
  const daysPassed = calculateDaysBetweenDates(today, dateOfCreation);

  useEffect(() => {
    const fetchAudioFile = async () => {
      if (daysPassed > 45) {
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        const response = await fetchWithAuthentication(
          `/interviews/audio?userId=${interview.userId}&interviewId=${interview.interviewId}`,
          "GET",
          null,
        )
          .then((response) => {
            if (!response.ok) {
              if (response.status === 404) {
                setAudioError("Oops! No audio exists for this interview.");
              }
              throw new InterviewResourceNotFoundError(
                `HTTP error! Status: ${response.status}`,
              );
            }
            return response.text(); // Get the presigned URL
          })
          .then((presignedUrl) => fetch(presignedUrl));
        if (response.ok) {
          const audioBlob = await response.blob();
          const audioObjectUrl = URL.createObjectURL(audioBlob);
          setAudioUrl(audioObjectUrl);
        } else {
          console.error("Failed to fetch the audio file:", response.statusText);
        }
      } catch (error) {
        console.error("Error fetching the audio file:", error);
        if (!(error instanceof InterviewResourceNotFoundError)) {
          setAudioError(
            "Unable to load audio. Please refresh the page and try again.",
          );
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchAudioFile();

    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAudio = () => {
    if (daysPassed >= 45) {
      return (
        <div style={{ justifyContent: "left", paddingLeft: ".6Rem" }}>
          Audio has been deleted due to the interview being over 45 days ago.
        </div>
      );
    }
    if (audioError || !audioUrl) {
      return (
        <div style={{ justifyContent: "left", paddingLeft: ".6Rem" }}>
          {audioError}
        </div>
      );
    }
    return <audio src={audioUrl || undefined} controls />;
  };

  return (
    <div className={audioError ? "" : "audioplayer-container"}>
      {isLoading ? (
        <ReactLoading type="spin" color="blue" height={30} width={30} />
      ) : (
        getAudio()
      )}
    </div>
  );
};

export default AudioPlayerComponent;
